<template>
  <div class="clearfix">
    <div
      id="nextReviewerModal"
      ref="nextReviewerModal"
      class="reveal next-reviewer-modal"
      data-reveal
    >
      <div class="tender-confirm clearfix">
        <div :class="{'required-error-highlight': inputRequireHighlight && !newReviewer}">
          <label class="next-reviewer-title">{{ $t('tenantProjects.submission.selectNextReviewer') }}</label>
          <HKLSelect
            :value="newReviewerId || undefined"
            class="next-reviewer-selector"
            :options="reviewerHklOptions"
            :showSearch="true"
            placeholder=""
            @change="onReviewerChange"
          />
        </div>
        <div
          v-if="techCommentActionTexts.length > 0"
          class="tech-reviewers-list"
        >
          <div class="reviewers-list-title">
            {{ $t('tenantProjects.submission.prevReviewerList') }}:
          </div>
          <ol>
            <li
              v-for="item in techCommentActionTexts"
              :key="item"
              class="reviewers-list-item"
            >
              {{ item }}
            </li>
          </ol>
        </div>
      </div>
      <div class="row align-right nav_btn">
        <button
          id="confirm_no"
          type="button"
          class="btn btn--darkblue"
          data-close
        >{{ $t('action.cancel') }}</button>
        <button
          id="confirm_ok"
          type="button"
          class="btn btn--orange"
          @click="changeAssignee()"
        >{{ $t('action.submit') }}</button>
      </div>
      <div
        v-if="invalidTechManager"
        lang="en"
        class="error-msg"
      >
        {{ $t('tenantProjects.submission.submittefFailedErrMsg') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import dateUtil from '@/utils/dateUtil'
import mutationTypes from '@/store/mutation-types'
import { CommentTypes, CommentActionTypes } from '@/modules/tenantProjects/submissions/consts'
import { DesignBatchStatus } from '@/utils/locale'
import { PermTypeMapKeys } from '@/utils/aclHelper'
import { getUsersForSameBuilding } from '../utils'

export default {
  components: {
    HKLSelect,
  },
  props: {
    confirmCallback: {
      type: Function,
      default: () => {},
    },
  },
  data () {
    return {
      inputRequireHighlight: false,
      nextReviewerOptions: [],
      newReviewer: '',
      newReviewerId: null,
      invalidTechManager: false,
    }
  },
  computed: {
    ...mapGetters('tenantProject', ['currentTenantSubmission', 'currentTenantProject', 'hasPendingSubmission', 'currentTechReviewer']),
    ...mapGetters('user', ['currentUser']),
    isTechPic () {
      return this.currentTechReviewer && this.currentTechReviewer.id === this.currentUser.id
    },
    designComment () {
      return JSON.parse(JSON.stringify(deep(this.currentTenantSubmission, 'designComment') || {}))
    },
    techCommentActionTexts () {
      return ((this.hasPendingSubmission && deep(this.currentTenantSubmission, 'actionHistory')) || [])
        .filter(rec => rec.actionType === CommentActionTypes.REVIEW && rec.commentTypes.includes(CommentTypes.TECH) && !rec.isNewRec).map(rec =>
          this.$t('workPermit.reviewedBy', [rec.user.displayName, dateUtil.format(rec.updatedAt, 'DD MMM YY HH:mm')]))
    },
    reviewerHklOptions () {
      return (this.nextReviewerOptions || []).map((opt) => ({
        name: opt.displayPicName || opt.displayName,
        value: opt.id,
      }))
    },
  },
  beforeDestroy () {
    if (this.nextReviewerModal) {
      this.nextReviewerModal.destroy()
    }
    $(this.$refs.nextReviewerModal).remove()
  },
  methods: {
    ...mapMutations('tenantProject', {
      updateFieldByPath: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_PATH,
    }),
    ...mapActions('tenantProject', ['loadTenantProject']),
    async init () {
      if (!this.nextReviewerModal) {
        this.nextReviewerModal = await new Foundation.Reveal($('#nextReviewerModal'), {
          closeOnEsc: false,
          closeOnClick: false,
        })
      }
    },
    async openModal () {
      await this.init()
      const permType = PermTypeMapKeys.TENANT_PROJECT_TECH_SEND
      const tenancy = this.currentTenantProject.tenancy
      this.nextReviewerOptions = await getUsersForSameBuilding(tenancy, permType, this.currentTechReviewer, { includeTrReps: true })
      this.nextReviewerOptions = this.nextReviewerOptions.sort((a, b) => a.displayName.localeCompare(b.displayName))
      this.newReviewer = ''
      this.newReviewerId = null
      this.nextReviewerModal.open()
    },
    closeModal () {
      if (this.nextReviewerModal) {
        this.nextReviewerModal.close()
      }
    },
    onReviewerChange (id) {
      this.newReviewerId = id || null
      this.newReviewer = (this.nextReviewerOptions || []).find((opt) => opt.id === id) || ''
    },
    async changeAssignee () {
      await this.validateTechMangager()
      if (this.invalidTechManager) {
        return
      }
      if (!this.newReviewer || !this.newReviewer.id) {
        this.inputRequireHighlight = true
        return
      }
      await this.confirmCallback(undefined, this.newReviewer.id)
      this.closeModal()
    },
    async validateTechMangager () {
      this.invalidTechManager = false
      await this.loadTenantProject(this.currentTenantProject.id)
      if (this.designComment.comment === DesignBatchStatus.DISAPPROVE) {
        this.invalidTechManager = true
        return false
      }
      return true
    },
  },
}
</script>

<style scoped lang="scss">
@import '~styles/settings';
@import '~styles/popup';
.tech-reviewers-list {
  margin-top:12px;
  ol {
    margin-bottom: 0;
  }
}
.reviewers-list-title, .reviewers-list-item {
  color: #878787;
  font-size: 12px;
  font-style: italic;
  padding: 0;
}
.reviewers-list-title {
  font-weight: bold;
}
.error-msg {
  color: red;
  text-align: justify;
  margin-top: 10px;
  -webkit-hyphens: auto;
  -moz-hyphens: auto;
  -ms-hyphens: auto;
  hyphens: auto;
}
</style>

<style lang="scss">
$tender-font-color: #0C3A58;
#nextReviewerModal {
  padding: 30px 56px;
  width: 525px;
  .next-reviewer-title, .next-reviewer-user {
    text-transform: capitalize;
    font-size: 14px;
    line-height: 1.43;
    font-weight: bold;
    text-align: left;
    color: $tender-font-color;
  }
  .next-reviewer-title {
    margin-top: 10px;
    margin-bottom: 10px;
  }
  .next-reviewer-user {
    margin-bottom: 5px;
  }
}
</style>
